import { Box, Grid, Typography } from '@mui/material'
import { useRootNodesQuery } from 'apollo/configurator/queries/RootNodes.generated'
import { BackFromEditorButton } from 'components/common/BackFromEditorButton'
import { ModelContext } from 'context/model/ModelContext'
import { lightTheme } from 'layout/theme/LightTheme'
import { useContext, useEffect, useState } from 'react'
import { Loading } from 'react-admin'
import { parseVersion } from 'resources/models/utils'
import AttributesEditor from '../molecules/manager/AttributesEditor'
import PublishModelToolbar from '../molecules/manager/PublishModelToolbar'
import TreeView from '../molecules/manager/TreeView'

const Manager = () => {
    const [nodeId, setNodeId] = useState<number | null>()
    const [modelId, setModelId] = useState<number | null>()
    const { data: { configuratorNodes: nodes } = {} } = useRootNodesQuery()
    const [parentId, setParentId] = useState<string>()
    const { state } = useContext(ModelContext)

    const handleNodeClick = (id: number | string, parentId: string) => {
        setNodeId(+id)
        setParentId(parentId)
    }

    useEffect(() => {
        if (+state.currentModel !== modelId) {
            setModelId(+state.currentModel)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.currentModel])

    const rootnode = nodes?.find((node: any) => node.id === modelId || +node.id === modelId)

    if (!nodes?.length) return <Loading />

    if (!state.currentModel) {
        return (
            <Box sx={{ padding: 4, textAlign: 'center' }}>
                <BackFromEditorButton isPristineForm={true} backToRouteURL="/models" />
                <Box sx={{ marginTop: 4 }}>
                    <h3>No model selected. Please select a model from the list.</h3>
                </Box>
            </Box>
        )
    }

    return (
        <ModelContext.Consumer>
            {() => (
                <Box sx={{ height: '100%', position: 'relative' }}>
                    <Grid
                        container
                        sx={{
                            position: 'sticky',
                            zIndex: 99,
                            backgroundColor: lightTheme.palette.background.default,
                            paddingBottom: 30,
                            flexDirection: 'row',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Grid
                            item
                            xs={12}
                            sx={{
                                position: 'sticky',
                                top: 50,
                                zIndex: 99,
                                backgroundColor: lightTheme.palette.background.default,
                                padding: '16px',
                                display: 'flex',
                                gap: 1.5
                            }}
                        >
                            <BackFromEditorButton
                                isPristineForm={!state.hasChanges}
                                backToRouteURL="/models"
                                warningMessage="Your changes have not been published to the configurator yet. Do you want to continue?"
                            />

                            <Box>
                                <Typography variant="body1">
                                    Model: <code>{state.currentModelCode}</code>
                                </Typography>

                                <Typography variant="subtitle2" component="small">
                                    Version: <code>{parseVersion(rootnode?.node_attributes)}</code>
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid
                            container
                            sx={{
                                position: 'fixed',
                                top: 150,
                                maxWidth: '100%',
                                width: 'auto',
                                minWidth: 'calc(100% - 230px)',
                                borderTop: '1px solid #eaeaea'
                            }}
                        >
                            <Grid
                                item
                                xs={6}
                                sx={{
                                    minWidth: '50%'
                                }}
                            >
                                {modelId ? (
                                    <>
                                        <TreeView
                                            handleNodeClick={handleNodeClick}
                                            rootNode={rootnode as any}
                                        />
                                    </>
                                ) : null}
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sx={{
                                    overflow: 'scroll',
                                    minWidth: '50%',
                                    maxHeight: 'calc(100vh - 155px)'
                                }}
                            >
                                {nodeId ? (
                                    <Box
                                        sx={{
                                            borderLeft: '1px solid #eaeaea',
                                            padding: ' 0 25px'
                                        }}
                                    >
                                        <AttributesEditor id={+nodeId} parentId={parentId} />
                                    </Box>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Grid>

                    <PublishModelToolbar modelId={modelId || null} />
                </Box>
            )}
        </ModelContext.Consumer>
    )
}

export default Manager
